import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export default function GenerateStuNoModal({
  setModalOpen,
  rows,
  setUserInfo,
  userInfo,
  idRule,
}) {
  const { t } = useTranslation();
  const isBirthdate8Rule = idRule === "birthdate8";

  const getSixDigitsFromBirthday = (birthday) => {
    const onlyDigits = birthday?.toString().replace(/\D/g, "") || "";

    if (onlyDigits.length >= 8) {
      return onlyDigits.slice(2, 8);
    }

    if (onlyDigits.length === 6) {
      return onlyDigits;
    }

    return "";
  };

  const [entranceDate, setEntranceDate] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [birthdateSixDigits, setBirthdateSixDigits] = useState(() =>
    getSixDigitsFromBirthday(userInfo?.birthday),
  );
  const [idAtSchool, setIdAtSchool] = useState("");

  const defaultStudentIdRuleMessage =
    "총 7자리로 입학년도 뒤2자리, 생년 뒤 2자리 + 생성순서 3자리입니다. 앞에 4자리 수를 갖고 있는 모든 학번을 찾아서 맨 마지막 학번의 뒷 세자리에서 더하기 1을 하는 방식으로 생성합니다.";
  const birthdate8RuleMessage =
    "총 8자리로 주민등록앞번호 6자리(생년월일) + 생성순서 2자리입니다. 앞에 6자리 수를 갖고 있는 모든 학번을 찾아서 맨 마지막 학번의 뒷 두자리에서 더하기 1을 하는 방식으로 생성합니다.";
  const studentIdRuleMessage = isBirthdate8Rule
    ? t("teacher.basicinfo.birthdate8StudentIdRule") || birthdate8RuleMessage
    : t("teacher.basicinfo.StudentIdMadeRule") || defaultStudentIdRuleMessage;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "birthdateSixDigits") {
      setBirthdateSixDigits(value.replace(/\D/g, "").slice(0, 6));
      setIdAtSchool("");
      return;
    }

    if (name === "entranceDate" && value.length === 2) {
      setEntranceDate(value);
    }

    if (name === "birthYear" && value.length === 2) {
      setBirthYear(value);
    }

    setIdAtSchool("");
    // console.log("rows", rows);
  };

  const generateBirthdate8Id = () => {
    if (birthdateSixDigits.length !== 6) {
      return;
    }

    const sequences = rows
      .map((r) => r.idatschool?.toString().trim())
      .filter((id) => {
        return id && /^\d{8}$/.test(id) && id.startsWith(birthdateSixDigits);
      })
      .map((id) => Number(id.slice(6, 8)))
      .filter((sequence) => Number.isInteger(sequence));
    const lastSequence = sequences.length > 0 ? Math.max(...sequences) : 0;
    const nextSequence = lastSequence + 1;

    if (nextSequence > 99) {
      alert(
        t("teacher.basicinfo.studentIdSequenceOverLimit") ||
          "생성 가능한 순번 99를 초과했습니다.",
      );
      setIdAtSchool("");
      return;
    }

    setIdAtSchool(
      birthdateSixDigits + nextSequence.toString().padStart(2, "0"),
    );
  };

  const handleClick = () => {
    if (isBirthdate8Rule) {
      generateBirthdate8Id();
      return;
    }

    if ((entranceDate + birthYear).length === 4) {
      const filteredRows = rows
        .filter((r) => {
          return (
            r.idatschool && r.idatschool.startsWith(entranceDate + birthYear)
          );
        })
        .sort((a, b) => {
          if (a.idatschool < b.idatschool) {
            return -1;
          }
          if (a.idatschool > b.idatschool) {
            return 1;
          }
          return 0;
        });

      if (filteredRows.length > 0) {
        const lastOne = filteredRows.slice(-1)[0];
        const newIdAtSchool = Number(lastOne.idatschool) + 1;
        let toText = newIdAtSchool.toString();
        if (toText.length < 7) {
          toText = "0" + toText;
        }
        setIdAtSchool(toText);
      } else {
        const newId = entranceDate + birthYear + "001";
        setIdAtSchool(newId);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setUserInfo((prevState) => ({
      ...prevState,
      idatschool: idAtSchool,
    }));
    setModalOpen(false);
  };

  return (
    <>
      <div
        className="modal-container"
        // onClick={() => {
        //   setModalOpen(false);
        // }}
      >
        <div className="modalForUnit">
          <div className="modal-header">
            <h3>{t("teacher.basicinfo.createStudentId") || "학번생성하기"}</h3>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              <div className="row mb-3">
                <div className="col-2">
                  <label htmlFor="explanation" className="col-form-label">
                    {t("teacher.basicinfo.howStudentIdMade") ||
                      "학번 생성 원리"}
                  </label>
                </div>
                <div className="col-10">
                  <textarea
                    name="explanation"
                    rows={5}
                    className="form-control"
                    value={studentIdRuleMessage}
                    readOnly
                  />
                </div>
              </div>

              {isBirthdate8Rule ? (
                <div className="row mb-3">
                  <div className="col-5 text-center">
                    <label
                      htmlFor="birthdateSixDigits"
                      className="col-form-label"
                    >
                      {t("teacher.basicinfo.sixDigitsOfBirthdate") ||
                        "생년월일 6자리"}
                    </label>
                  </div>
                  <div className="col-4">
                    <input
                      required
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      pattern="\d{6}"
                      name="birthdateSixDigits"
                      className="form-control"
                      value={birthdateSixDigits}
                      placeholder="예시: 131010"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="row mb-3">
                    <div className="col-5 text-center">
                      <label htmlFor="entranceDate" className="col-form-label">
                        {t("teacher.basicinfo.twoDigitsOfEntranceYear") ||
                          "입학년도 뒤 2자리"}
                      </label>
                    </div>
                    <div className="col-4">
                      <input
                        required
                        type="number"
                        name="entranceDate"
                        className="form-control"
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-5 text-center">
                      <label htmlFor="birthYear" className="col-form-label">
                        {t("teacher.basicinfo.twoDigitsOfBirthYear") ||
                          "생년도 뒤 2자리"}
                      </label>
                    </div>
                    <div className="col-4">
                      <input
                        required
                        type="number"
                        name="birthYear"
                        className="form-control"
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-5 text-center">
                      <label htmlFor="fourDigits" className="col-form-label">
                        {t("teacher.basicinfo.fourDigitsOfStudentId") ||
                          "학번 앞 4자리"}
                      </label>
                    </div>
                    <div className="col-5">
                      <input
                        type="text"
                        name="fourDigits"
                        className="form-control"
                        value={entranceDate + birthYear}
                        readOnly
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="row mb-3">
                <div className="col-5 text-center">
                  <label
                    htmlFor="generatedIdatSchool"
                    className="col-form-label"
                  >
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      onClick={handleClick}
                    >
                      {t("teacher.basicinfo.clickToCreateStudentId") ||
                        "학번생성 클릭"}
                    </button>
                  </label>
                </div>
                <div className="col-auto">
                  <input
                    required
                    type="text"
                    name="generatedIdatSchool"
                    className="form-control"
                    value={idAtSchool}
                    readOnly
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                {t("common.labels.cancel") || "취소"}
              </button>
              <button type="submit" className="btn btn-primary">
                {t("teacher.basicinfo.insert") || "입력하기"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
